// OtterBonk, the co-op run in the OtterBonk hut (zone "bonk"). Walking into the hut opens OtterBonk's own welcome page
// full screen (public/otterbonk/welcome.js): the hut's rooms (src/office/games/otterbonk.ts): 创建房间 makes one with me
// as its host, the others 加入 / 离开 (up to 4), each member picks a class and presses 准备, and the host 开始 once
// everyone else is ready. A room that is playing shows 进行中 and its players get 回到游戏. Each room's last result, my
// personal best, how to play and the game's settings are there too. Its 离开 (or Esc) goes back to the office: the otter
// is still in the hut, and a small card there opens the page again (walking out and in again does too). The run itself
// is its own page-in-a-page over it: the office says "ob_go" to the room's players, and this mounts the game full screen
// (public/otterbonk/client.js, loaded only then). 离开 closes it: the otter is still in the hut, the voice there keeps
// going, and 回到游戏 rejoins while the run is on. While the run's code and art load and its first frames are drawn, the
// loading screen (public/loading.js) covers it (only if that takes over 250 ms).
// 新手教程 (public/otterbonk/tutorial.js): the first time the welcome page opens for me (and from its 新手教程 button),
// a short scripted run on this device only, mounted the same way (the run's page over the welcome page), before any
// real run. It closes by itself when a real run of mine starts or I walk out.
import { mountWelcome, roomName } from '/otterbonk/welcome.js';
import { t, tr, errorText } from '/i18n.js';
import { createLoading } from '/loading.js';
import { tourSeen } from '/tour.js';

export function initOtterBonk({ send, myId, myName = () => '', lookOf = () => null, lobby, el, inZone, hutCount, stopWalking, fulls = [], walkToZone = () => false, guest = () => false }) {
  const B = { rooms: [], best: null, progress: null, office: null, note: '', heroEpoch: null, stale: false };
  const sendGame = (m) => send({ ...m, heroEpoch: 2 });
  const compatible = () => B.heroEpoch === 1 || B.heroEpoch === 2;
  let pendingGo = null;
  // the small card in the hut while the welcome page is closed: 打开大冒险
  const card = lobby('oblobby', 'OtterBonk', () => t('ob.hut.rules'), 'sword', 'otterbonk');
  card.see.remove();
  tr(card.go, 'ob.hut.open');
  let game = null, mounting = false, loading = null;
  /** The 新手教程 up (createTutorial's), and whether it was offered this visit to the page by itself already. */
  let tut = null, tutOffered = false, goAfter = null;
  /** The welcome page: open while I'm in the hut, unless I closed it (dismissed: until I walk out and in again). */
  const W = { open: false, dismissed: false, wasHere: false, key: '' };
  // An invite to a room (app.js / guest.js kept ?ob=<room id> for this tab): walk to the hut, then into the room
  // (it stays in sessionStorage until used, refused or timed out: a sign-in or a reload on the way keeps it)
  const J = { id: null, tries: 0, triedAt: 0, at: 0, walking: false };
  try { J.id = sessionStorage.getItem('ob.join'); } catch {}
  const joinDone = () => { J.id = null; try { sessionStorage.removeItem('ob.join'); } catch {} };
  function invited(here) {
    if (!J.id || !myId()) return;
    if (!J.at) J.at = Date.now(); // (from when I'm aboard)
    if (Date.now() - J.at > 180e3) { joinDone(); return; } // three minutes and more: forget it
    // not in the hut: walk (and sail) there; tried again every 5 s if the way wasn't found, 3 times at most
    if (!here) {
      if (!J.walking && J.tries < 3 && Date.now() - J.triedAt > 5000) {
        const id = J.id; J.triedAt = Date.now(); J.walking = true;
        void Promise.resolve(walkToZone('bonk')).then((started) => {
          if (J.id !== id) return;
          if (started) J.tries = 3;
          else if (started !== null && ++J.tries === 3) { joinDone(); note(t('bonk.join.cantReach', { play: t('floors.name.play') })); }
        }).finally(() => { J.walking = false; });
      }
      return;
    }
    if (!B.seen || !compatible()) return; // wait for a compatible hut before consuming the invite
    const r = B.rooms.find((x) => x.id === J.id), me = myId();
    joinDone();
    if (!r) { note(t('ob.hut.inviteGone')); return; }
    if (r.players.some((p) => p.id === me)) return;
    if (r.busy || r.players.length >= 4) { note(t(r.busy ? 'ob.hut.inviteBusy' : 'ob.hut.inviteFull', { room: roomName(r) })); return; }
    // (the hut moves me out of my room when the join goes through; refused, I keep it)
    sendGame({ t: 'ob_room_join', id: r.id });
  }
  const note = (text) => { B.note = text; W.key = ''; render(); setTimeout(() => { if (B.note === text) { B.note = ''; W.key = ''; render(); } }, 6000); };
  /** 复制邀请链接: this page's address (its floor) with the room; 邀请访客: a guest invite (1 小时, 4 次) carrying it. */
  async function invite(r, asGuest) {
    let url = '';
    if (asGuest) {
      const j = await fetch('/api/guests', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ room: 'main', ttl: '1h', max: 4, label: t('ob.hut.guestLabel', { room: roomName(r) }) }) }).then((x) => x.json()).catch(() => null);
      if (!j?.ok) { note(t('ob.hut.guestFail')); return; }
      const fl = new URL(location.href).searchParams.get('floor');
      url = `${location.origin}/guest#o=main${j.srv ? `&s=${encodeURIComponent(j.srv)}` : ''}&i=${encodeURIComponent(j.id)}&t=${encodeURIComponent(j.t || '')}&ob=${encodeURIComponent(r.id)}${fl && /^[0-9]{1,2}$/.test(fl) ? `&f=${fl}` : ''}`;
    } else { const u = new URL(location.href); u.search = ''; const f = new URL(location.href).searchParams.get('floor'); if (f) u.searchParams.set('floor', f); u.searchParams.set('ob', r.id); u.hash = ''; url = u.href; }
    try { await navigator.clipboard.writeText(url); note(t(asGuest ? 'ob.hut.guestCopied' : 'ob.hut.linkCopied')); }
    catch { window.prompt(t('ob.hut.copyPrompt'), url); }
  }
  const page = mountWelcome({
    myId, lookOf, canInviteGuests: () => !guest(), touch: () => matchMedia('(pointer: coarse)').matches,
    onClose: () => { W.open = false; W.dismissed = true; render(); },
    act: (what, a) => {
      B.note = '';
      if (!compatible() && what !== 'tutorial') { note(t('ob.hut.stale')); return; }
      if (what === 'new') sendGame({ t: 'ob_room_new' });
      else if (what === 'join') sendGame({ t: 'ob_room_join', id: a });
      else if (what === 'leaveRoom') sendGame({ t: 'ob_room_leave' });
      else if (what === 'class') sendGame({ t: 'ob_class', cls: a });
      else if (what === 'progress') sendGame({ t: 'ob_progress_get' });
      else if (what === 'ready') sendGame({ t: 'ob_ready', on: a });
      else if (what === 'start') sendGame(a ? { t: 'ob_start', resume: true } : { t: 'ob_start' });
      else if (what === 'rejoin') mount(B.office, a.runId);
      else if (what === 'tutorial') { void mount(null, null, true); return; }
      else if (what === 'invite') { void invite(a, false); return; }
      else if (what === 'guestInvite') { void invite(a, true); return; }
      W.key = ''; render();
    },
  });
  // Esc / keys while it is up: games.js gives them to the open full-screen page (the otter doesn't walk out under it)
  // (not while the run is loading over it: an Esc then mustn't close the page for after the run)
  fulls.push({ isOpen: () => page.shown(), close: () => { if (!mounting) page.back(); }, focus: () => { if (!mounting) page.primary(); } });

  /** The run's page over the welcome page: a real run (office, runId), or the 新手教程 (tutorial: on this device). A
   *  real run of mine starting while the tutorial is up takes its place. */
  async function mount(office, runId, tutorial = false) {
    if (game && tut && !tutorial) endTutorial();
    if (mounting && !tutorial) { goAfter = [office, runId]; return; } // (the tutorial still loading: this run once it's up)
    if (game || mounting) return;
    mounting = true;
    const L = (loading = createLoading({ title: t(tutorial ? 'ob.hut.loadTutorial' : 'ob.lobby.title') }));
    const code = L.task(2), art = L.task(2), warm = L.task(1);
    L.show();
    try {
      const [{ mountOtterBonk }, T] = await L.track(Promise.all([import('/otterbonk/client.js'), tutorial ? import('/otterbonk/tutorial.js') : null]), code);
      code.done();
      stopWalking(); // a key held down while the game opens must not keep walking the otter out of the hut
      if (tutorial) {
        const me = myId(), l = lookOf(me), mine = B.rooms.find((r) => r.players.some((p) => p.id === me));
        const t = (tut = T.createTutorial({ name: myName() || undefined, look: l?.look ?? null, seed: l?.seed ?? me, cls: (({ cls2, cls } = {}) => cls2 ?? cls ?? 0)(mine?.players.find((p) => p.id === me)) }));
        game = mountOtterBonk({ local: t.socket, onTutorial: () => void mount(null, null, true), onLeave: () => { L.close(); t.stop(); if (tut === t) { tut = null; game = null; } render(); }, onPreload: (load) => L.phaser(load, art) });
        const g = game;
        // 开始真正的游戏 (done): back on the welcome page, in a room (a new one if I'm in none) to start from
        t.begin(g, (done) => { if (tut !== t) return; tut = null; L.close(); if (game === g) { game = null; g.destroy(); } if (done && compatible() && !B.rooms.some((r) => r.players.some((p) => p.id === myId()))) sendGame({ t: 'ob_room_new' }); W.key = ''; render(); });
      } else game = mountOtterBonk({ office, runId, heroEpoch: B.heroEpoch, onTutorial: () => void mount(null, null, true), onLeave: () => { L.close(); game = null; render(); }, onPreload: (load) => L.phaser(load, art) });
      const g = game;
      L.ready(() => g.settled?.() ?? true, { cap: 4000, task: warm }); // the roster and gear come with the run's welcome
    } catch (e) { L.close(); console.error('OtterBonk failed to load', e); B.note = t(e instanceof SyntaxError ? 'ob.hut.stale' : 'ob.hut.loadFail'); } // (SyntaxError: a new client.js against the modules this page loaded before a deploy)
    finally { mounting = false; render(); }
    if (goAfter) { const [o, r] = goAfter; goAfter = null; void mount(o, r); }
  }
  function endTutorial() { const t = tut, g = game; tut = null; t?.stop(); if (g) { game = null; g.destroy(); } }

  function render() {
    const here = inZone('bonk');
    if (!here && game) { loading?.close(); if (tut) endTutorial(); else { game.destroy(); game = null; } } // walked away (e.g. moved by the office): no game over the map
    if (here && !W.wasHere && !W.dismissed) { W.open = true; stopWalking(); } // walked in: the welcome page opens
    if (!here) { W.open = false; W.dismissed = false; tutOffered = false; }
    W.wasHere = here;
    invited(here);
    const showPage = here && W.open && !game;
    page.show(showPage);
    // the first time the page opens for me (on this device or my account): the 新手教程 before anything else (once a
    // visit: closed, it waits for the 新手教程 button). Not on the way into a room I was invited to.
    if (showPage && !tutOffered && !mounting && !J.id && B.seen && !tourSeen('ob-tutorial') /* tutorial.js DONE */ && !tourSeen('otterbonk') /* played before (its old HUD tour) */ && !B.rooms.some((r) => r.busy && r.players.some((p) => p.id === myId()))) { tutOffered = true; void mount(null, null, true); }
    card.d.style.display = here && !W.open && !game ? '' : 'none';
    if (!here || game) return;
    card.count.textContent = t('ob.hut.count', { n: hutCount('bonk') });
    const me = myId(), mine = B.rooms.find((r) => r.players.some((p) => p.id === me));
    card.status.textContent = B.note || (mine ? t(mine.busy ? 'ob.room.busy' : 'ob.hut.inRoom', { room: roomName(mine) }) : B.rooms.length ? t('ob.hut.rooms', { n: B.rooms.length }) : t('ob.hut.noRooms'));
    card.go.onclick = () => { W.open = true; W.dismissed = false; stopWalking(); render(); };
    if (!showPage) return;
    page.count(hutCount('bonk'));
    // the page is only redrawn when what it shows changed (this runs 4 times a second): the rooms, my best, everyone's
    // look (装扮), the hour (the save's countdown); not while a finger or button is down on it (the press would be lost)
    const looks = B.rooms.flatMap((r) => r.players.map((p) => lookOf(p.id))).concat(lookOf(me));
    const key = JSON.stringify([B.rooms, B.best, B.progress, B.heroEpoch, B.note, looks, Math.floor(Date.now() / 36e5)]);
    if (key !== W.key && !page.pressed()) { W.key = key; page.render({ rooms: B.rooms, best: B.best, progress: B.progress, heroEpoch: B.heroEpoch, note: B.note }); }
  }

  function msg(m) {
    if (m.t === 'ob_state') {
      if ((B.office && B.office !== m.office) || (B.heroEpoch !== null && B.heroEpoch !== m.heroEpoch)) B.progress = null;
      B.rooms = m.rooms ?? []; B.office = m.office; B.seen = true; B.heroEpoch = m.heroEpoch === 1 || m.heroEpoch === 2 ? m.heroEpoch : 0;
      if (!compatible()) { pendingGo = null; B.progress = null; B.stale = true; B.note = t('ob.hut.stale'); }
      else {
        if (B.stale) { B.stale = false; B.note = ''; }
        if (pendingGo) { const [o, r, epoch] = pendingGo; pendingGo = null; if (o === B.office && epoch === B.heroEpoch) void mount(o, r); }
      }
      render();
    }
    else if (m.t === 'ob_best') { B.best = m.best; render(); }
    else if (m.t === 'ob_progress') { if (compatible() && m.heroEpoch === B.heroEpoch) B.progress = m.progress ?? null; render(); }
    else if (m.t === 'ob_go') {
      if (m.heroEpoch !== 1 && m.heroEpoch !== 2) note(t('ob.hut.stale'));
      else if (B.office !== m.office || B.heroEpoch !== m.heroEpoch) pendingGo = [m.office, m.runId, m.heroEpoch];
      else { B.note = ''; void mount(m.office, m.runId); }
    }
    else if (m.t === 'ob_msg') { const note = B.note = errorText('bonk.err', { code: m.code, message: m.text, params: m.params }); render(); setTimeout(() => { if (B.note === note) { B.note = ''; render(); } }, 4000); } // bonk.err.<code>
  }
  return { msg, render, isOpen: () => !!game };
}
